import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Button, CircularProgress, Tooltip, Snackbar, Alert } from '@mui/material';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import { styled } from '@mui/material/styles';
import generateDeficiencyPdf from './utils/generateDeficiencyPdf';
import { fetchProjectById } from '../ProjectDashboard/services/projectDashboardService';

const StyledButton = styled(Button)(() => ({
    textTransform: 'none',
    borderRadius: 8,
    padding: '8px 16px',
    fontWeight: 500,
    minWidth: 'auto',
}));

/**
 * Export PDF button for the deficiency page toolbar.
 * Generates a professional PDF of all currently filtered deficiencies.
 *
 * @param {Object} props
 * @param {Array} props.filteredDeficiencies - Currently filtered deficiency list
 * @param {Object} props.filters - Active filter state object
 * @param {Function} props.getImageUrl - Function to convert image key to full URL
 * @param {string} props.projectId - Project ID for fetching project info
 * @param {boolean} props.showDeferredCost - Whether deferred-cost fields should be included
 */
const ExportButton = ({
    filteredDeficiencies,
    filters,
    getImageUrl,
    projectId,
    showDeferredCost = true,
}) => {
    const [exporting, setExporting] = useState(false);
    const [error, setError] = useState(null);

    const handleExport = async () => {
        setExporting(true);
        setError(null);
        try {
            let projectInfo = null;
            if (projectId) {
                try {
                    projectInfo = await fetchProjectById(projectId);
                } catch {
                    // Continue without project info if fetch fails
                }
            }
            await generateDeficiencyPdf({
                deficiencies: filteredDeficiencies,
                filters,
                getImageUrl,
                projectInfo,
                showDeferredCost,
            });
        } catch (err) {
            console.error('PDF export failed:', err);
            setError('PDF export failed. Please try again.');
        } finally {
            setExporting(false);
        }
    };

    const disabled = exporting || filteredDeficiencies.length === 0;

    return (
        <>
            <Tooltip title={disabled && !exporting ? 'No deficiencies to export' : 'Download PDF'}>
                <span>
                    <StyledButton
                        variant="outlined"
                        onClick={handleExport}
                        disabled={disabled}
                        aria-label="Download deficiency report as PDF"
                        sx={{
                            borderColor: (theme) => theme.palette.grey[200],
                            color: (theme) => theme.palette.text.primary,
                            '&:hover': {
                                borderColor: (theme) => theme.palette.primary.main,
                                color: (theme) => theme.palette.primary.main,
                            },
                        }}
                    >
                        {exporting ? (
                            <CircularProgress size={20} color="inherit" />
                        ) : (
                            <DownloadOutlinedIcon sx={{ fontSize: 20 }} />
                        )}
                    </StyledButton>
                </span>
            </Tooltip>
            <Snackbar
                open={!!error}
                autoHideDuration={5000}
                onClose={() => setError(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={() => setError(null)} severity="error" variant="filled">
                    {error}
                </Alert>
            </Snackbar>
        </>
    );
};

ExportButton.propTypes = {
    filteredDeficiencies: PropTypes.array.isRequired,
    filters: PropTypes.object.isRequired,
    getImageUrl: PropTypes.func.isRequired,
    projectId: PropTypes.string.isRequired,
    showDeferredCost: PropTypes.bool,
};

export default ExportButton;
